import moment, { unitOfTime } from "moment";
import { DateType, DateValueType } from "react-tailwindcss-datepicker";
import { GetFiltersType } from "src/modules/modules.type";
import {
  myTaskCheckListConnectionWithTasksListName,
  myTaskCheckListDirectionsListName,
  myTaskCheckListResultsListName,
  myTaskCheckListsListName,
  myTaskCheckListTasksListName,
  myTaskShiftListName,
} from "src/api/config/meetings.config";
import { addItemRequest, getItemsRequest, updateItemRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { ListFieldsType } from "src/api/config";
import {
  ChecklistShiftFilters,
  ChecklistTaskFilters,
  CompleteChecklistShiftPayload,
  ReturnChecklistShiftPayload,
  ShiftItem,
  TaskItem,
} from "./types";
import {
  getResultByTaskStatus,
  getShiftPeriodReference,
  getStartAndEndDateByType,
  getStartEndDateByPeriod,
  isChecklistDateBeforeEditableWindow,
} from "./utils";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";

export async function getCheckListsByFilters(filters: Partial<GetFiltersType<typeof myTaskCheckListsListName>>) {
  const { items } = await getItemsRequest({
    listName: myTaskCheckListsListName,
    filters,
  });
  return items;
}

export async function getCheckListsWithTasksByFilters(
  filters: Partial<GetFiltersType<typeof myTaskCheckListConnectionWithTasksListName>>,
) {
  const { items } = await getItemsRequest({
    listName: myTaskCheckListConnectionWithTasksListName,
    filters,
  });

  return items.sort((a, b) => (!!a.sort && !!b.sort ? a.sort.localeCompare(b.sort) : 0));
}

export async function getTasksByFilters(filters: Partial<GetFiltersType<typeof myTaskCheckListTasksListName>>) {
  const { items } = await getItemsRequest({
    listName: myTaskCheckListTasksListName,
    filters,
  });

  return items;
}

export async function getCheckListResultWithTaskByFilters(
  filters: Partial<GetFiltersType<typeof myTaskCheckListResultsListName>>,
) {
  const { items } = await getItemsRequest({
    listName: myTaskCheckListResultsListName,
    filters,
    isLocalDate: true,
  });

  return items;
}

type ListTaskType = ListFieldsType[typeof myTaskCheckListTasksListName];

export type TaskType = {
  ID: number;
  Title: string;
  Description: string;
  CompletionCriteria: string;
  Direction: ListTaskType["Direction"];
  Test: string;
  ResponsibleEmail: string;
};

export async function getTaskByID(id: string): Promise<TaskType> {
  const { items } = await getItemsRequest({
    listName: myTaskCheckListTasksListName,
    filters: { ID: id },
  });

  const [task] = modifiedItemsState(items, myTaskCheckListTasksListName);

  return {
    ID: task.ID,
    Title: task.Title,
    Description: task.Description,
    CompletionCriteria: task.CompletionCriteria,
    Direction: task.Direction,
    Test: task.Test,
    ResponsibleEmail: task.ResponsibleEmail,
  };
}

export async function fetchActionsCountByChecklist(checkListId: number) {
  const { items } = await getItemsRequest({
    listName: myTaskActionsListName,
    filters: {
      ImportantHashTag: `#чек-лист_${checkListId}`,
    },
  });

  return items.length;
}

export async function buildTaskItemFromConnection(params: {
  connection: any;
  targetEmail: string;
  currentUserEmail: string;
  periodDate: DateValueType;
  isShiftCompletedForDate?: boolean;
  taskResultItems?: any[];
  taskItems?: any[];
  countActions?: number;
}) {
  const {
    connection,
    targetEmail,
    currentUserEmail,
    periodDate,
    isShiftCompletedForDate = false,
    taskResultItems: prefetchedTaskResultItems,
    taskItems: prefetchedTaskItems,
    countActions: prefetchedCountActions,
  } = params;

  if (!periodDate?.startDate) {
    return null;
  }

  const targetDate = periodDate.startDate;

  if (connection.EndDate) {
    const endDate = getStartEndDateByPeriod(connection.TaskPeriod, connection.EndDate);

    if (moment.utc(targetDate).isAfter(moment(endDate.end))) {
      return null;
    }
  }

  if (!isConnectionWithinPeriod(connection, targetDate)) {
    return null;
  }

  const completedDate = getStartEndDateByPeriod(connection.TaskPeriod, targetDate);

  const taskResultItems =
    prefetchedTaskResultItems ??
    (await getCheckListResultWithTaskByFilters({
      EmployeeEmail: targetEmail,
      ConnectionID: connection.ID,
      CompletedDate: completedDate,
    }));

  const [taskResultItem] = taskResultItems;

  let taskResult = getResultByTaskStatus(taskResultItem);

  const isDeleted =
    moment(targetDate).isSameOrAfter(moment(connection.EndDate).startOf("day").toDate()) &&
    connection.Title === "Задача удалена";

  if (isDeleted) {
    return null;
  }

  let isDisabled = currentUserEmail !== targetEmail || isChecklistDateBeforeEditableWindow(targetDate);

  if (isShiftCompletedForDate) {
    isDisabled = true;
  }

  if (!taskResultItem) {
    if (shouldDisableByDeadline(targetDate as Date, connection.TaskPeriod)) {
      isDisabled = true;
    }
  }

  const taskItems =
    prefetchedTaskItems ??
    (await getTasksByFilters({
      ID: connection.Task.ID || connection.Task.get_lookupId(),
    }));

  if (!taskItems.length) {
    return null;
  }

  const [task] = modifiedItemsState(taskItems, myTaskCheckListTasksListName);

  if (
    connection.ResponsibleEmail !== "all" &&
    !(connection.ResponsibleEmail === targetEmail || connection.ResponsibleEmail === currentUserEmail)
  ) {
    return null;
  }

  const countActions =
    prefetchedCountActions ?? (await fetchActionsCountByChecklist(connection.CheckList.get_lookupId()));

  const passTest =
    task.Test.length === 0
      ? "---"
      : taskResultItem?.Test && JSON.parse(taskResultItem.Test).length > 0
        ? "Пройден"
        : "Не пройден";

  const answers = taskResultItem?.Test ? JSON.parse(taskResultItem.Test) : [];

  const newTaskItem: TaskItem = {
    ID: task.ID,
    ResponsibleEmail: connection.ResponsibleEmail,
    AuthorEmail: connection.Author.get_email() || "",
    Test: task.Test ? JSON.parse(task.Test) : [],
    PassTest: passTest,
    Direction: task.Direction.Title,
    Period: connection.TaskPeriod,
    CheckListID: connection.CheckList.get_lookupId(),
    ConnectionID: connection.ID,
    Task: {
      Title: task.Title,
      Description: task.Description,
      CompletionCriteria: task.CompletionCriteria,
    },
    Answers: answers,
    EndDate: connection.EndDate ? moment(connection.EndDate).toDate() : null,
    Status: taskResultItem?.TaskStatus,
    Result: taskResult,
    disabled: isDisabled,
    countActions,
    isDeleted: connection.Title === "Задача удалена",
  };

  return newTaskItem;
}

export async function fetchChecklistTasksDataset(params: ChecklistTaskFilters) {
  const { email: targetEmail, jobTitle, periodDate, currentUserEmail } = params;
  const shiftsPromise = fetchChecklistShiftDataset({
    email: targetEmail,
    date: periodDate?.startDate,
  });

  if (!jobTitle || !periodDate?.startDate) {
    return { tasks: [], shifts: await shiftsPromise };
  }
  const targetDate = periodDate.startDate;

  const [checkListItems, shifts] = await Promise.all([
    getCheckListsByFilters({
      UserPosition: jobTitle + "*",
    }),
    shiftsPromise,
  ]);

  if (!checkListItems.length) {
    return { tasks: [], shifts };
  }

  const checkListIDs = checkListItems.map((c) => c.ID);

  const checkListsWithTaskItems = await getCheckListsWithTasksByFilters({
    CheckList: checkListIDs,
    ResponsibleEmail: ["all", targetEmail],
  });

  if (!checkListsWithTaskItems.length) {
    return { tasks: [], shifts };
  }

  const connectionIds = checkListsWithTaskItems.map((connection) => connection.ID);
  const taskIds = Array.from(
    new Set(checkListsWithTaskItems.map((connection) => Number(connection.Task.get_lookupId())).filter(Boolean)),
  );
  const checkListIDsForActions = Array.from(
    new Set(checkListsWithTaskItems.map((connection) => Number(connection.CheckList.get_lookupId())).filter(Boolean)),
  );

  const [taskResults, taskDefinitions, actionsResponse] = await Promise.all([
    getCheckListResultWithTaskByFilters({
      EmployeeEmail: targetEmail,
      ConnectionID: connectionIds,
    }),
    getTasksByFilters({ ID: taskIds }),
    getItemsRequest({
      listName: myTaskActionsListName,
      filters: {
        ImportantHashTag: checkListIDsForActions.map((id) => `#чек-лист_${id}`),
      },
    }),
  ]);

  const taskDefinitionsById = new Map(taskDefinitions.map((task) => [Number(task.ID), task]));
  const taskResultsByConnectionId = taskResults.reduce<Map<number, any[]>>((result, taskResult) => {
    const connectionId = Number(taskResult.ConnectionID?.get_lookupId?.());
    if (connectionId) {
      result.set(connectionId, [...(result.get(connectionId) || []), taskResult]);
    }
    return result;
  }, new Map());
  const actionsCountByCheckListId = actionsResponse.items.reduce<Map<number, number>>((result, action) => {
    const match = String(action.ImportantHashTag || "").match(/^#чек-лист_(\d+)$/);
    if (match) {
      const checkListId = Number(match[1]);
      result.set(checkListId, (result.get(checkListId) || 0) + 1);
    }
    return result;
  }, new Map());

  const newItemsArray: TaskItem[] = [];
  const isShiftCompletedForDate = shifts.some((shift) => shift.Finished);

  for (const connection of checkListsWithTaskItems) {
    const completedDate = getStartEndDateByPeriod(connection.TaskPeriod, targetDate);
    const connectionResults = (taskResultsByConnectionId.get(Number(connection.ID)) || []).filter((taskResult) =>
      moment(taskResult.CompletedDate).isBetween(
        moment(completedDate.start),
        moment(completedDate.end),
        undefined,
        "[]",
      ),
    );
    const taskDefinition = taskDefinitionsById.get(Number(connection.Task.get_lookupId()));
    const checkListId = Number(connection.CheckList.get_lookupId());
    const taskItem = await buildTaskItemFromConnection({
      connection,
      targetEmail,
      currentUserEmail,
      periodDate,
      isShiftCompletedForDate,
      taskResultItems: connectionResults,
      taskItems: taskDefinition ? [taskDefinition] : [],
      countActions: actionsCountByCheckListId.get(checkListId) || 0,
    });

    if (taskItem) {
      newItemsArray.push(taskItem);
    }
  }

  return { tasks: newItemsArray, shifts };
}

export async function fetchChecklistShiftDataset(params: ChecklistShiftFilters): Promise<ShiftItem[]> {
  const { email, date } = params;

  if (!email || !date) {
    return [];
  }

  const selectedDate = moment(date);

  if (!selectedDate.isValid()) {
    return [];
  }

  const day = selectedDate.format("YYYY-MM-DD");
  const { items } = await getItemsRequest({
    listName: myTaskShiftListName,
    filters: {
      ResponsibleEmail: email,
      Date: {
        start: day,
        end: day,
      },
    },
  });

  return items.map((item) => ({
    ...(item as ShiftItem),
    Date: moment(item.Date).format("YYYY-MM-DD"),
    Finished: item.Finished,
  }));
}

export async function completeChecklistShiftRequest(params: CompleteChecklistShiftPayload) {
  const { email, date, comment } = params;

  if (!email || !date) {
    return null;
  }

  const selectedDate = moment(date);

  if (!selectedDate.isValid()) {
    return null;
  }

  const day = selectedDate.format("YYYY-MM-DD");
  const commentValue = comment?.trim() ?? "";
  const { items } = await getItemsRequest({
    listName: myTaskShiftListName,
    filters: {
      ResponsibleEmail: email,
      Date: {
        start: day,
        end: day,
      },
    },
  });
  const existingShift = items[0];
  const body: Partial<ListFieldsType[typeof myTaskShiftListName]> = {
    ResponsibleEmail: email,
    Date: day,
    Finished: true,
    Comment: commentValue,
  };

  if (existingShift?.ID) {
    const result = await updateItemRequest({
      listName: myTaskShiftListName,
      body: {
        ID: existingShift.ID,
        Finished: true,
        Comment: commentValue,
      },
    });

    return Array.isArray(result) ? Number(result[0]) : Number(result);
  }

  const result = await addItemRequest({
    listName: myTaskShiftListName,
    body,
  });

  return Number(result);
}

export async function returnChecklistShiftToWorkRequest(params: ReturnChecklistShiftPayload) {
  if (!params.ID) {
    return null;
  }

  return updateItemRequest({
    listName: myTaskShiftListName,
    body: {
      ID: params.ID,
      Finished: false,
    },
  });
}

export async function fetchChecklistTaskByConnection(params: {
  connectionId: number;
  periodDate: DateValueType;
  email: string;
  currentUserEmail: string;
}) {
  const { connectionId, periodDate, email, currentUserEmail } = params;
  const { items } = await getItemsRequest({
    listName: myTaskCheckListConnectionWithTasksListName,
    filters: { ID: connectionId },
  });

  if (!items.length) {
    return null;
  }

  // ВАЖНО: тут намеренно НЕ используется modifiedItemsState — buildTaskItemFromConnection
  // (как и массовый fetchChecklistTasksDataset) работает с "сырым" объектом connection,
  // где CheckList/Task/Author — это lookup-объекты с методами get_lookupId()/get_email().
  // modifiedItemsState превращает их в уже готовые плоские объекты {ID, Title}/{ID, Name, Email}
  // без этих методов, из-за чего buildTaskItemFromConnection падал с ошибкой
  // "connection.CheckList.get_lookupId is not a function" при открытии задачи по ID.
  const [connection] = items;
  const shifts = await fetchChecklistShiftDataset({
    email,
    date: periodDate?.startDate,
  });
  const isShiftCompletedForDate = shifts.some((shift) => shift.Finished);

  const taskItem = await buildTaskItemFromConnection({
    connection,
    targetEmail: email,
    currentUserEmail,
    periodDate,
    isShiftCompletedForDate,
  });

  return taskItem;
}

function isConnectionWithinPeriod(connection: any, targetDate?: DateType | null) {
  if (!targetDate) {
    return true;
  }

  const creationDate = moment(connection.Created);
  const periodStartMap: Record<string, unitOfTime.StartOf> = {
    Неделя: "isoWeek",
    Месяц: "month",
    Квартал: "quarter",
    Год: "year",
  };

  const type = periodStartMap[connection.TaskPeriod] ?? "day";
  const { start } = getStartAndEndDateByType(creationDate, type);

  return start.isSameOrBefore(getShiftPeriodReference(targetDate));
}

function shouldDisableByDeadline(targetDate: Date | null | undefined, period: string) {
  if (!targetDate) return false;
  const mapping: Record<string, unitOfTime.StartOf> = {
    Неделя: "isoWeek",
    Месяц: "month",
    Квартал: "quarter",
    Год: "year",
  };

  const type = mapping[period] ?? "day";
  const { end } = getStartAndEndDateByType(targetDate, type);

  const deadlineEndDate = end.clone().add(1, "week").isBefore(new Date());

  return deadlineEndDate;
}
